import React, { useContext, useEffect, useState } from "react";
import { firestore } from "./../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import moment from "moment";
import "./../styles/blog.css";
import { WebsiteContext } from "../context/AppContext";
import Loader from "react-js-loader";
import HelmetComponent from "../components/common/HelmetComponent";
import { useNavigate } from "react-router-dom";
import { IoTimeOutline, IoCalendarOutline, IoArrowForward } from "react-icons/io5";
import { motion } from "framer-motion";

const defaultArticles = [
  {
    _id: "default-1",
    title: "5 Daily Habits to Eliminate Hesitation and Speak Fluent English",
    category: "Speaking Tips",
    excerpt: "Overcoming fear of making grammar mistakes is 80% of the battle. Discover actionable micro-habits that train your tongue to articulate thoughts effortlessly.",
    cover_picture: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=700&q=80",
    time_to_read: 5,
    formattedDate: "October 10, 2026",
    language: "English",
  },
  {
    _id: "default-2",
    title: "How to Build an Active Word Bank with Synonyms & Antonyms",
    category: "Vocabulary",
    excerpt: "Stop repeating 'very good' and 'nice'. Learn how pairing new words with contrasting antonyms builds mental pathways for instant recall during spontaneous dialogue.",
    cover_picture: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=700&q=80",
    time_to_read: 4,
    formattedDate: "October 08, 2026",
    language: "English",
  },
  {
    _id: "default-3",
    title: "Mastering Tricky Phrasal Verbs in Professional Workplaces",
    category: "Grammar & Career",
    excerpt: "Phrasal verbs are the secret sauce to sounding like a native speaker. Here are the top 15 conversational verbs you will encounter in international meetings.",
    cover_picture: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=700&q=80",
    time_to_read: 6,
    formattedDate: "October 05, 2026",
    language: "English",
  },
];

const categories = ["All", "Speaking Tips", "Vocabulary", "Grammar & Career"];

const Blog = () => {
  const [blogData, setBlogData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const { color, dark } = useContext(WebsiteContext);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchBlogPosts = async () => {
    try {
      const blogCollection = collection(firestore, "blogs");
      const blogSnapshot = await getDocs(blogCollection);
      const blogList = blogSnapshot.docs.map((doc) => {
        const data = doc.data();
        let formattedDate = "Recent";
        if (data.created_at?.seconds) {
          formattedDate = moment(new Date(data.created_at.seconds * 1000)).format("MMMM D, YYYY");
        }
        return {
          ...data,
          formattedDate,
          category: data.category || "English Mastery",
        };
      });

      if (blogList.length > 0) {
        setBlogData(blogList);
      } else {
        setBlogData(defaultArticles);
      }
    } catch (error) {
      console.warn("Using curated articles (Firestore offline):", error);
      setBlogData(defaultArticles);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const handleBlogDetails = (id) => {
    navigate(`/blogs/${id}/blog_details`);
  };

  const filteredPosts =
    selectedCategory === "All"
      ? blogData
      : blogData.filter((post) => post.category === selectedCategory);

  return (
    <>
      <HelmetComponent
        title="Blog & English Resources | Fluent Fable"
        name="description"
        content="Read actionable English fluency tips, vocabulary insights, grammar guides, and speaking strategies from Fluent Fable language experts."
      />

      <div className={`blog_page_wrapper ${dark ? "blog_page_dark" : ""}`}>
        {/* Header */}
        <div className="blog_hero_header">
          <span className={`blog_badge ${dark ? "blog_badge_dark" : "blog_badge_light"}`}>
            📚 Fluent Fable Journal
          </span>
          <h1 className="blog_main_title" style={{ color: color }}>
            Insights, Stories & Fluency Guides
          </h1>
          <p className="blog_main_subtitle" style={{ color: color }}>
            Actionable techniques, vocabulary deep-dives, and mindset shifts to take you from hesitant speaker to commanding communicator.
          </p>
        </div>

        {/* Filter Pills */}
        <div className="blog_filter_bar">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`filter_pill ${selectedCategory === cat ? "filter_pill_active" : ""}`}
              onClick={() => setSelectedCategory(cat)}
              style={{ color: color }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Loader type="spinner-cub" size={40} bgColor={color} color={color} />
          </div>
        )}

        {/* Blog Cards Grid */}
        {!loading && (
          <div className="blog_cards_grid">
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post._id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.1 }}
                className={`modern_blog_card ${dark ? "blog_card_dark" : "blog_card_light"}`}
                onClick={() => handleBlogDetails(post._id)}
              >
                {/* Cover Image */}
                <div className="card_cover_wrap">
                  <img
                    src={post.cover_picture || defaultArticles[0].cover_picture}
                    alt={post.title}
                    className="card_cover_img"
                    loading="lazy"
                  />
                  <span className="card_category_badge">{post.category}</span>
                </div>

                {/* Body Content */}
                <div className="card_body">
                  <div>
                    <div className="card_meta_row">
                      <span className="meta_item">
                        <IoCalendarOutline size={14} /> {post.formattedDate}
                      </span>
                      <span className="meta_item">
                        <IoTimeOutline size={14} /> {post.time_to_read || 5} min read
                      </span>
                    </div>

                    <h2 className="card_title" style={{ color: color }}>
                      {post.title}
                    </h2>

                    <p className="card_excerpt" style={{ color: color }}>
                      {post.excerpt || post.content?.slice(0, 110) + "..."}
                    </p>
                  </div>

                  <div className="card_footer_row">
                    <span>Read Article</span>
                    <span className="read_more_cta">
                      <IoArrowForward size={16} />
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default Blog;
